import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  TrendingUp,
  TrendingDown,
  BarChart3,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { useToast } from '../../common/Toast';
import API_CONFIG from '../../../config/api';
import PageBreadcrumb from '../../common/PageBreadcrumb';
import PageHeader from '../../common/PageHeader';
import '../ProjectDashboardReportsStandalone.css';

const FinancialReports = () => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    totalBudget: 0,
    totalSpent: 0,
    budgetVariance: 0,
    budgetData: []
  });

  useEffect(() => {
    loadReportData();
  }, []);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/dashboard`, {
        headers: API_CONFIG.HEADERS
      });

      if (response.ok) {
        const data = await response.json();
        const totalBudget = data.totalBudget || 0;
        const totalSpent = data.totalSpent || 0;
        const budgetVariance = totalBudget - totalSpent;
        
        setReportData({
          totalBudget,
          totalSpent,
          budgetVariance,
          budgetData: data.budgetData || []
        });
      } else {
        setReportData({
          totalBudget: 2500000,
          totalSpent: 1800000,
          budgetVariance: 700000,
          budgetData: [
            { name: 'Digital Transformation', budget: 500000, actual: 450000, variance: -50000 },
            { name: 'Infrastructure Upgrade', budget: 300000, actual: 320000, variance: 20000 }
          ]
        });
        showError('Failed to load financial data. Using sample data.');
      }
    } catch (err) {
      console.error('Error loading financial report data:', err);
      setReportData({
        totalBudget: 2500000,
        totalSpent: 1800000,
        budgetVariance: 700000,
        budgetData: []
      });
      showError('Error loading financial report data. Using sample data.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <PageBreadcrumb />
        <PageHeader title="Financial Reports" description="Budget analysis and financial performance" />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading financial reports...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Financial Reports"
        description="Budget analysis, cost tracking, and financial performance insights"
      />
      <div className="page-container">
        {/* Summary Cards */}
        <div className="summary-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
          <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-1">${(reportData.totalBudget / 1000000).toFixed(1)}M</h3>
                  <p className="page-kpi-label">Total Budget</p>
                </div>
                <div className="page-kpi-icon-container color-1">
                  <DollarSign size={24} className="page-kpi-icon color-1" />
                </div>
              </div>
            </div>
          </div>
          <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className="page-kpi-value color-2">${(reportData.totalSpent / 1000000).toFixed(1)}M</h3>
                  <p className="page-kpi-label">Total Spent</p>
                </div>
                <div className="page-kpi-icon-container color-2">
                  <BarChart3 size={24} className="page-kpi-icon color-2" />
                </div>
              </div>
            </div>
          </div>
          <div className="card h-100 page-kpi-card" style={{ margin: 0 }}>
            <div className="card-body page-kpi-body">
              <div className="page-kpi-content">
                <div className="page-kpi-text">
                  <h3 className={`page-kpi-value ${reportData.budgetVariance >= 0 ? 'color-3' : 'color-4'}`}>
                    ${Math.abs(reportData.budgetVariance / 1000000).toFixed(1)}M
                  </h3>
                  <p className="page-kpi-label">Variance</p>
                </div>
                <div className={`page-kpi-icon-container ${reportData.budgetVariance >= 0 ? 'color-3' : 'color-4'}`}>
                  {reportData.budgetVariance >= 0 ? (
                    <TrendingUp size={24} className="page-kpi-icon" />
                  ) : (
                    <TrendingDown size={24} className="page-kpi-icon" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Budget Table */}
        {reportData.budgetData && reportData.budgetData.length > 0 && (
          <div style={{ padding: '1.5rem 2rem', background: 'white', marginTop: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>Project Budget Analysis</h3>
            <div className="table-header" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
              <div className="table-header-cell">Project</div>
              <div className="table-header-cell">Budget</div>
              <div className="table-header-cell">Actual</div>
              <div className="table-header-cell">Variance</div>
              <div className="table-header-cell">Status</div>
            </div>
            <div className="table-body">
              {reportData.budgetData.map((project, index) => (
                <div key={index} className="table-row" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
                  <div className="table-cell">{project.name}</div>
                  <div className="table-cell">${project.budget?.toLocaleString() || 0}</div>
                  <div className="table-cell">${project.actual?.toLocaleString() || 0}</div>
                  <div className="table-cell">
                    <span className={project.variance >= 0 ? 'variance-over' : 'variance-under'}>
                      ${Math.abs(project.variance || 0).toLocaleString()}
                      {project.variance >= 0 ? ' Over' : ' Under'}
                    </span>
                  </div>
                  <div className="table-cell">
                    <span className={`status-badge ${project.variance >= 0 ? 'status-over-budget' : 'status-on-track'}`}>
                      {project.variance >= 0 ? 'Over Budget' : 'On Track'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FinancialReports;